import { Auth0Client } from '@auth0/auth0-spa-js';
import { LocalStorageManager } from 'services/storage/LocalStorageManager';
import type { TokenErrorReason } from 'shared/domain/auth/tokenErrorReason';
import { getErrorMessage } from 'shared/utils/errorHandling';
import { isFreshToken, TOKEN_FRESHNESS_THRESHOLD_MS, type TokenPayload } from 'shared/domain/auth/tokenModel';

export type { TokenErrorReason } from 'shared/domain/auth/tokenErrorReason';

export class TokenBrokerError extends Error {
  readonly reason: TokenErrorReason;

  constructor(reason: TokenErrorReason, message?: string) {
    super(message ?? reason);
    this.name = 'TokenBrokerError';
    this.reason = reason;
  }
}

export interface TokenBrokerDeps {
  getAuth0Client: () => Auth0Client | undefined;
  saveToken: (forceRefresh?: boolean) => Promise<void>;
}

const AUTH_DEGRADED_COOLDOWN_MS = 30_000;

let deps: TokenBrokerDeps | null = null;
let inflightRefresh: Promise<TokenPayload> | null = null;
let inflightForce = false;
let authDegradedUntil = 0;
let refreshGeneration = 0;
let rejectInflightRefresh: ((error: TokenBrokerError) => void) | null = null;

function isAuthDegraded(): boolean {
  return Date.now() < authDegradedUntil;
}

function setAuthDegraded(): void {
  authDegradedUntil = Date.now() + AUTH_DEGRADED_COOLDOWN_MS;
}

export function clearAuthDegraded(): void {
  authDegradedUntil = 0;
}

function getCachedTokenIfFresh(): TokenPayload | null {
  const token = LocalStorageManager.getAuthToken();
  const expirationRaw = LocalStorageManager.getAuthTokenExpiration();
  const tokenExpiration = expirationRaw ? parseFloat(expirationRaw) : 0;
  const payload = { token, tokenExpiration };

  if (isFreshToken(payload)) {
    return payload;
  }

  return null;
}

// Auth0 errors carry a code such as login_required, timeout or missing_refresh_token.
function describeAuth0Error(error: unknown): string {
  if (
    error &&
    typeof error === 'object' &&
    'error' in error &&
    typeof error.error === 'string'
  ) {
    return error.error;
  }
  return getErrorMessage(error) ?? 'unknown';
}

async function fetchFreshToken(force: boolean): Promise<TokenPayload> {
  const generation = refreshGeneration;

  if (!deps) {
    throw new TokenBrokerError(
      'auth0_unavailable',
      'Token broker not initialized'
    );
  }

  if (isAuthDegraded()) {
    throw new TokenBrokerError('degraded', 'Token refresh cooldown active');
  }

  const client = deps.getAuth0Client();
  if (!client) {
    setAuthDegraded();
    throw new TokenBrokerError('auth0_unavailable', 'Auth0 client not ready');
  }

  let authenticated: boolean;
  try {
    authenticated = await client.isAuthenticated();
  } catch {
    setAuthDegraded();
    throw new TokenBrokerError(
      'auth0_unavailable',
      'Auth0 isAuthenticated failed'
    );
  }

  if (!authenticated) {
    throw new TokenBrokerError('unauthenticated');
  }

  try {
    const cachedExpiration = LocalStorageManager.getAuthTokenExpiration();
    const cachedExpirationMs = cachedExpiration
      ? parseFloat(cachedExpiration)
      : 0;
    const nowPlusThreshold = Date.now() + TOKEN_FRESHNESS_THRESHOLD_MS;
    const shouldForce =
      force || !cachedExpirationMs || cachedExpirationMs <= nowPlusThreshold;
    await deps.saveToken(shouldForce);
  } catch (error) {
    if (generation !== refreshGeneration) {
      throw new TokenBrokerError('unauthenticated', 'Refresh cancelled');
    }
    setAuthDegraded();
    throw new TokenBrokerError(
      'refresh_failed',
      `saveToken failed: ${describeAuth0Error(error)}`
    );
  }

  if (generation !== refreshGeneration) {
    throw new TokenBrokerError('unauthenticated', 'Refresh cancelled');
  }

  const token = LocalStorageManager.getAuthToken();
  const tokenExpirationRaw = LocalStorageManager.getAuthTokenExpiration();
  const tokenExpiration = tokenExpirationRaw
    ? parseFloat(tokenExpirationRaw)
    : 0;
  const payload = { token, tokenExpiration };

  if (!isFreshToken(payload)) {
    setAuthDegraded();
    throw new TokenBrokerError(
      'stale_token',
      'Token still stale after refresh'
    );
  }

  clearAuthDegraded();
  return payload;
}

export function configureTokenBroker(tokenDeps: TokenBrokerDeps): void {
  deps = tokenDeps;
}

export async function getFreshToken(options?: {
  force?: boolean;
}): Promise<TokenPayload> {
  const force = options?.force ?? false;

  // A fresh token another tab saved is good even during this tab's cooldown.
  if (!force) {
    const cached = getCachedTokenIfFresh();
    if (cached) {
      return cached;
    }
  }

  if (isAuthDegraded()) {
    throw new TokenBrokerError('degraded', 'Token refresh cooldown active');
  }

  if (inflightRefresh) {
    if (!force || inflightForce) {
      return inflightRefresh;
    }
    try {
      await inflightRefresh;
    } catch {
      // A failed non-force refresh should not block a forced retry.
    }
  }

  inflightForce = force;
  inflightRefresh = new Promise<TokenPayload>((resolve, reject) => {
    rejectInflightRefresh = reject;
    fetchFreshToken(force).then(resolve, reject);
  }).finally(() => {
    rejectInflightRefresh = null;
    inflightRefresh = null;
    inflightForce = false;
  });

  return inflightRefresh;
}

export function cancelTokenRefreshOnLogout(): void {
  refreshGeneration += 1;
  rejectInflightRefresh?.(
    new TokenBrokerError('unauthenticated', 'Refresh cancelled')
  );
  rejectInflightRefresh = null;
  inflightRefresh = null;
  inflightForce = false;
  clearAuthDegraded();
}

/** Test-only reset */
export function resetTokenBrokerForTests(): void {
  deps = null;
  rejectInflightRefresh?.(
    new TokenBrokerError('unauthenticated', 'Refresh cancelled')
  );
  rejectInflightRefresh = null;
  inflightRefresh = null;
  inflightForce = false;
  authDegradedUntil = 0;
  refreshGeneration = 0;
}

/** Test-only helper */
export function setAuthDegradedForTests(untilMs: number): void {
  authDegradedUntil = untilMs;
}
